import { api } from '../api/client';
import { uploadBlob } from '../api/blobUpload';
import {
  commitManifestMutation,
  decryptFolderManifest,
  encryptFolderManifest,
  fileBlobRefsFromManifest,
  mergeManifestEntries,
  renameFileInManifest,
  type ManifestCommitResult,
  type ManifestPathConflict,
  type SharedFolderFileEntry,
  type SharedFolderManifestPlaintext,
} from './sharedFolderManifest';

export interface DeletedFileItem {
  historyEntryId: string;
  deletedAt: string;
  purgeAt: string;
  entry: SharedFolderFileEntry;
  actorKind: string | null;
  actorId: string | null;
}

export async function listDeletedSharedFolderFiles(
  folderId: string,
  folderKey: Uint8Array,
  currentManifest: SharedFolderManifestPlaintext,
): Promise<DeletedFileItem[]> {
  const history = await api.getSharedFolderDeletionHistory(folderId);
  const currentPaths = new Set(currentManifest.entries.map((entry) => entry.path));
  const currentFileIds = new Set(
    currentManifest.entries
      .filter((entry) => entry.type === 'file')
      .map((entry) => entry.fileId),
  );
  const deletedByPath = new Map<string, DeletedFileItem>();

  for (const item of history) {
    const blob = await api.fetchBlob(item.manifestBlobId);
    const oldManifest = await decryptFolderManifest(folderKey, blob);
    for (const entry of oldManifest.entries) {
      if (entry.type !== 'file') {
        continue;
      }

      if (currentPaths.has(entry.path) || currentFileIds.has(entry.fileId)) {
        continue;
      }

      const existing = deletedByPath.get(entry.path);
      if (!existing || item.deletedAt > existing.deletedAt) {
        deletedByPath.set(entry.path, {
          historyEntryId: item.id,
          deletedAt: item.deletedAt,
          purgeAt: item.purgeAt,
          entry,
          actorKind: item.actorKind,
          actorId: item.actorId,
        });
      }
    }
  }

  return [...deletedByPath.values()].sort((left, right) => right.deletedAt.localeCompare(left.deletedAt));
}

export async function revertDeletedSharedFolderFile(input: {
  folderId: string;
  folderKey: Uint8Array;
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  deletedItem: DeletedFileItem;
}): Promise<ManifestCommitResult> {
  const { manifest: merged, conflicts } = mergeManifestEntries(
    input.manifest,
    [input.deletedItem.entry],
  );
  const manifestBlobId = await uploadBlob(await encryptFolderManifest(input.folderKey, merged));
  const committed = await api.revertSharedFolderDeletion(input.folderId, {
    expectedRevision: input.revision,
    manifestBlobId,
    fileBlobRefs: fileBlobRefsFromManifest(merged),
    historyEntryId: input.deletedItem.historyEntryId,
  });

  return {
    revision: committed.revision,
    manifest: merged,
    conflicts,
  };
}

export async function renameSharedFolderFile(input: {
  folderId: string;
  folderKey: Uint8Array;
  revision: number;
  manifest: SharedFolderManifestPlaintext;
  filePath: string;
  newName: string;
}): Promise<ManifestCommitResult> {
  let conflicts: ManifestPathConflict[] = [];
  const result = await commitManifestMutation(
    input.folderId,
    input.folderKey,
    input.revision,
    input.manifest,
    (latest) => {
      const renamed = renameFileInManifest(latest, input.filePath, input.newName);
      conflicts = renamed.conflicts;
      return renamed.manifest;
    },
  );

  return { ...result, conflicts };
}
